import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderSlotButton } from './arrival-order-slot-button.component';

import { Button } from '@/components/ui/button/button.component';
import { cn } from '@/utils/cn-tw-merge';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { dateFormatter } from '@/utils/formatters/date';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';

export const ARRIVAL_ORDER_COLLAPSED_LIMIT = 4;

interface ArrivalOrderDayColumnProps {
  date: string;
  slots: SchedulesSearchSlot[];
  expanded: boolean;
  busySlotIds?: ReadonlySet<string>;
  today?: Date;
  slotClassName?: string;
  onToggleExpand: () => void;
  onSlotClick?: (slot: SchedulesSearchSlot) => void;
}

export function ArrivalOrderDayColumn({
  date,
  slots,
  expanded,
  busySlotIds,
  today = new Date(),
  slotClassName,
  onToggleExpand,
  onSlotClick,
}: ArrivalOrderDayColumnProps) {
  const dateStr = dateFormatter(date, 'YYYY-MM-DD');
  const todayStr = dateFormatter(today, 'YYYY-MM-DD');
  const weekday = dateStr === todayStr
    ? 'Hoje'
    : formatToTitleCase(dateFormatter(date, 'weekday') ?? '').replace(/\.$/, '');
  const dayMonth = dateFormatter(date, 'DD/MM');

  const visibleSlots = expanded ? slots : slots.slice(0, ARRIVAL_ORDER_COLLAPSED_LIMIT);
  const showToggle = slots.length > ARRIVAL_ORDER_COLLAPSED_LIMIT;

  return (
    <div className="flex flex-1 flex-col items-center gap-2 min-w-0">
      <div
        className="sticky top-0 w-full flex flex-col items-center text-center whitespace-nowrap"
        data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_DAY_HEADER}
      >
        <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-semibold">
          {weekday}
        </Typography>
        <Typography variant={TypographyVariant.BODY_TEXT}>
          {dayMonth}
        </Typography>
      </div>
      {visibleSlots.map((slot) => {
        const start = dateFormatter(slot.start, 'HH:mm');
        const end = dateFormatter(slot.end, 'HH:mm');
        return (
          <ArrivalOrderSlotButton
            key={slot.id}
            start={start}
            end={end}
            busy={busySlotIds?.has(slot.id)}
            className={cn('w-full', slotClassName)}
            onClick={onSlotClick ? () => onSlotClick(slot) : undefined}
          />
        );
      })}
      {showToggle && (
        <Button
          variant="text"
          startIcon={expanded ? faMinus : faPlus}
          startIconClassName="text-green-500"
          onClick={onToggleExpand}
          className="gap-2"
          data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_TOGGLE_EXPAND}
        >
          {expanded ? 'Mostrar menos' : 'Ver mais horários'}
        </Button>
      )}
    </div>
  );
}
